import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Crown, MapPin } from "lucide-react";

const INOV_IMG = {
  blackVelvet: "https://customer-assets.emergentagent.com/job_transylvanian-bread/artifacts/n1t0n17e_New%20Ad%202026%20002.png",
  dracula: "https://customer-assets.emergentagent.com/job_transylvanian-bread/artifacts/fje36ho6_New%20Ad%202026%20004.png",
};

export default function InovatieCrossPromo() {
  return (
    <section data-testid="inovatie-crosspromo" className="relative py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B0B0B] via-[#1a0000] to-[#0B0B0B]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(139,0,0,0.3),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(201,164,92,0.15),transparent_60%)]" />

      <div className="relative max-w-7xl mx-auto container-px">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C9A45C]/50 bg-black/60">
            <Sparkles className="h-3.5 w-3.5 text-[#C9A45C]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A45C] font-semibold">Ediție 2026 • Inovație DOTAPAN</span>
          </div>
          <h2 className="font-display mt-5 text-3xl md:text-5xl text-white leading-tight">
            Două produse iconice pentru <em className="text-[#8B0000] not-italic">HoReCa premium.</em>
          </h2>
          <p className="mt-4 text-base text-white/75 max-w-2xl mx-auto">
            Croissante spectaculoase, memorabile, ideale pentru cafenele premium, hoteluri, restaurante tematice și zone turistice.
          </p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {/* Black Velvet */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="group relative rounded-3xl overflow-hidden border border-white/10 bg-black hover:border-[#C9A45C]/50 transition-all"
            data-testid="crosspromo-black-velvet"
          >
            <div className="relative h-72 md:h-96 overflow-hidden">
              <img
                src={INOV_IMG.blackVelvet}
                alt="Black Velvet Croissant"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 border border-[#C9A45C]/50 backdrop-blur text-[#C9A45C]">
                <Crown className="h-3 w-3" />
                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold">Premium Signature</span>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="font-display text-2xl md:text-3xl text-white">
                  Black Velvet <span className="text-[#C9A45C]">Croissant</span>
                </h3>
                <p className="mt-2 text-sm text-white/75 max-w-md">Eleganță întunecată. Gust rafinat. Extrem de fotogenic.</p>
              </div>
            </div>
          </motion.div>

          {/* Dracula's Favorite */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="group relative rounded-3xl overflow-hidden border border-[#8B0000]/40 bg-black hover:border-[#8B0000] hover:shadow-[0_30px_60px_-20px_rgba(139,0,0,0.5)] transition-all"
            data-testid="crosspromo-dracula"
          >
            <div className="relative h-72 md:h-96 overflow-hidden">
              <img
                src={INOV_IMG.dracula}
                alt="Dracula's Favorite Croissant"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 border border-[#8B0000]/60 backdrop-blur text-white">
                <MapPin className="h-3 w-3 text-[#8B0000]" />
                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold">Made in Transylvania</span>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="font-display text-2xl md:text-3xl leading-tight text-[#8B0000] drop-shadow-[0_2px_20px_rgba(139,0,0,0.6)]">
                  Dracula&apos;s <span className="text-white">Favorite</span>
                </h3>
                <p className="mt-2 text-sm text-white/80 max-w-md">Produs iconic pentru zonele turistice. Viral pe social media.</p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <Link to="/inovatie" data-testid="crosspromo-cta">
            <button className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-gradient-to-b from-[#8B0000] to-[#5c0000] text-white text-sm font-semibold border border-[#8B0000] hover:brightness-110 hover:-translate-y-0.5 transition-all shadow-[0_15px_40px_-10px_rgba(139,0,0,0.6)]">
              Descoperă pagina Inovație <ArrowRight className="h-4 w-4" />
            </button>
          </Link>
          <Link to="/contact">
            <button className="inline-flex items-center gap-2 h-12 px-7 rounded-full border border-[#C9A45C]/50 text-[#C9A45C] hover:bg-[#C9A45C]/10 hover:border-[#C9A45C] transition-all text-sm">
              Cere ofertă B2B
            </button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
